import { Button, TooltipProvider } from '@monksflow/monks-ui';
import { Plus, Redo, Trash2, Undo } from 'lucide-react';
import { EmojiPicker } from '@/shared/ui';
import { useAnnotationDraft } from '../model/comments.store';
import type { AnnotatedCommentMediaKind } from '../model/annotation';
import { AnnotationToolButton } from './AnnotationToolButton';
import { IconActionButton } from './IconActionButton';
import { useTranslation } from 'react-i18next';

interface Props {
  assetKind: AnnotatedCommentMediaKind;
  showAnnotationToolbar?: boolean;
  /**
   * True when this composer currently owns the annotation tool selection
   * (i.e. it was the last to be focused). When false, the annotation
   * popover trigger still renders (so the user can claim ownership by
   * clicking) but the active-tool styling is suppressed — the tool
   * visually belongs to another composer.
   */
  isToolActive?: boolean;
  /**
   * Where the undo / redo / clear actions render. When true, they live
   * inside the annotation popover only (edit composers — keeps the
   * inline row narrow enough to fit under the avatar inset). When
   * false (default), they render inline next to the annotation tool
   * button (the always-visible new-comment composer).
   */
  showDraftActions?: boolean;
  onInsertText: (text: string) => void;
  onOpenFilePicker: () => void;
  onCancel: () => void;
  canAttach: boolean;
  showCancel: boolean;
  cancelDisabled?: boolean;
  isSubmitting: boolean;
}

export function CommentInputActionsBar({
  assetKind,
  showAnnotationToolbar,
  isToolActive = false,
  showDraftActions = false,
  onInsertText,
  onOpenFilePicker,
  onCancel,
  canAttach,
  showCancel,
  cancelDisabled = false,
  isSubmitting,
}: Props) {
  const { t } = useTranslation('projects');

  const { draftShapes, redoStack, undoDraft, redoDraft, clearDrafts } = useAnnotationDraft();
  const hasDrafts = isToolActive && draftShapes.length > 0;
  const canRedo = redoStack.length > 0;

  return (
    <TooltipProvider delayDuration={300}>
      <div className="flex w-full items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <IconActionButton
            size="sm"
            label={t('commentOnFile.commentInputActionsBar.attachFile')}
            Icon={Plus}
            onClick={onOpenFilePicker}
            disabled={!canAttach}
          />
          <EmojiPicker onSelect={onInsertText} />
          {showAnnotationToolbar && (
            <>
              <AnnotationToolButton
                assetKind={assetKind}
                isActive={isToolActive}
                showDraftActions={showDraftActions}
              />
              {!showDraftActions && hasDrafts && (
                <>
                  <IconActionButton
                    size="sm"
                    label={t('commentOnFile.commentInputActionsBar.undoAnnotation')}
                    Icon={Undo}
                    onClick={undoDraft}
                  />
                  <IconActionButton
                    size="sm"
                    label={t('commentOnFile.commentInputActionsBar.redoAnnotation')}
                    Icon={Redo}
                    onClick={redoDraft}
                    disabled={!canRedo}
                  />
                  <IconActionButton
                    size="sm"
                    label={t('commentOnFile.commentInputActionsBar.clearAnnotations')}
                    Icon={Trash2}
                    onClick={clearDrafts}
                  />
                </>
              )}
            </>
          )}
        </div>

        <div className="flex items-center gap-2">
          {showCancel && (
            <Button
              aria-label={t('commentOnFile.commentInputActionsBar.cancel')}
              size="sm"
              variant="outline"
              disabled={isSubmitting || cancelDisabled}
              onClick={onCancel}
            >
              {t('commentOnFile.commentInputActionsBar.cancel')}
            </Button>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}
